import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { DetailSection, LabelText } from '../../../../../components/generic';
import InputModal from '../../../../../components/modals/InputModal';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext, useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useApproveReceiptsMutation,
  useDeleteReceiptsMutation,
  useDenyReceiptsMutation,
  useGetAllReceiptsQuery
} from '../../../../../services/endpoints/finance/receipt';
import { Scopes, useGetUsersQuery, User } from '../../../../../services/endpoints/people/user';
import { useGetCoursesSimpleQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { Receipt } from '../../../../../types/finance.types';
import { receiptsColumns } from '../../../../../utils/consts/columnDefs/financeColumns';
import { sessionTypeNumbers } from '../../../../../utils/consts/schedule/sessions';
import { canSeeFinancials, userTypes } from '../../../../../utils/consts';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';
import { getErrorMessage } from '../../../../../utils/utils';
import {Forbidden} from "../../../../../components/error/Forbidden";

const defaultScope = 'pending';

// RBAC-FE-203: Global Admin excluded from Finance per policy [H]
// Note: Cannot use user?.isAdmin because isAdmin() helper includes global_admin (user_type 3)
// Must explicitly check user_type_id to exclude Global Admin
// Includes instructor for receipt submission (view their own receipts)
export const userCanAdmin_Receipts = (user: User | null) =>
    !!(user?.isSuper ||
        user?.isSupremeAdmin ||
        user?.user_type_id === userTypes.admin ||
        user?.isLocationAdmin ||
        user?.isInstructor);

export const userCanManage_Receipts = (user: User | null) =>
    user?.user_type_id !== undefined && canSeeFinancials(user.user_type_id);

export const ReceiptsView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const canAccessReceipts = userCanAdmin_Receipts(currentUser);
  const canManageReceipts = userCanManage_Receipts(currentUser);
  const { data, error, isLoading, isFetching } = useGetAllReceiptsQuery(filters, {
    skip: !canAccessReceipts
  });
  const [denyReceipts] = useDenyReceiptsMutation();
  const [approveReceipts] = useApproveReceiptsMutation();
  const [deleteReceipts] = useDeleteReceiptsMutation();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const {
    data: courses,
    error: coursesError
  } = useGetCoursesSimpleQuery(undefined, { skip: !canAccessReceipts });
  const {
    data: users,
    error: usersError
  } = useGetUsersQuery({
    scope: 'colleagues',
    data: { entries: 10000, orderBy: { field: 'first_name', order: 'asc' } }
  }, { skip: !canAccessReceipts });

  const filterFormFields: FilterField[] = [
    {
      name: 'course_id',
      type: 'number',
      element: (
          <FormSelect
              name="course_id"
              label="Session"
              options={
                  courses?.results?.map((c: { id: number; title: string }) => ({
                    value: c.id,
                    label: c.title
                  })) ?? []
              }
              loadingError={coursesError ? 'Error loading courses' : undefined}
              selected_Value={courses?.results
                  ?.map((c: { id: number; title: string }) => ({ value: c.id, label: c.title }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id'))}
          />
      )
    },
    {
      name: 'user_id',
      type: 'number',
      element: (
          <FormSelect
              name="user_id"
              label="Instructor"
              options={
                  users?.results?.map((u: { id: number; full_name: string }) => ({
                    value: u.id,
                    label: u.full_name
                  })) ?? []
              }
              loadingError={usersError ? 'Error loading users' : undefined}
              selected_Value={users?.results
                  ?.map((u: { id: number; full_name: string }) => ({ value: u.id, label: u.full_name }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'user_id'))}
          />
      )
    },
    {
      name: 'date_of_purchase_from',
      id: 'purchased_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="date_of_purchase_from"
              label="Date of Purchase"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'purchased_at', 'greater_equal_than')
    },
    {
      name: 'date_of_purchase_to',
      id: 'purchased_at',
      operator: 'lesser_equal_than',
      element: (
          <DateFilterField name="date_of_purchase_to" />
      ),
      value: getFilterValue(filters.data?.filterBy, 'purchased_at', 'lesser_equal_than')
    },
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          type: 'number' as const,
          element: (
              <FormSelect
                  name="location_id"
                  label="Location"
                  options={locationOptions}
                  selected_Value={locationOptions.find(
                      (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                  )}
              />
          )
        }]
      : []),
    {
      name: 'course_type',
      type: 'number',
      element: (
          <FormSelect
              name="course_type"
              label="Session Type"
              options={Object.entries(sessionTypeNumbers).map((s) => ({ value: +s[0], label: s[1] }))}
              selected_Value={Object.entries(sessionTypeNumbers)
                  .map((s) => ({ value: +s[0], label: s[1] }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_type'))}
          />
      )
    },
    {
      name: 'total',
      label: 'Total',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'total')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="created_at_from"
              label="Created At"
          />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: (
          <DateFilterField name="created_at_to" />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setReceipts(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const handleDenyReceipts = async (reason: string) => {
    try {
      await denyReceipts({
        ids: selectedIDs,
        reason_for_denial: reason
      }).unwrap();
      addToast(`Receipts #${selectedIDs.join(',')} denied`, 'success');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  // RBAC-FE-203: Defense-in-depth guard for finance pages
  if (!canAccessReceipts) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Receipts`}
            columns={receiptsColumns}
            data={receipts}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            indexDownloadPath="receipts"
            sidebars={[
              <DetailSection title="Summary">
                <LabelText label="Total:">
                  <CurrencyCell value={data?.totals?.total ?? 0} />
                </LabelText>
              </DetailSection>
            ]}
            filters={filters?.data?.filterBy}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
              />
            }
            onFiltersChanged={setFilters}
            selectableRows={canManageReceipts && filters.scope !== 'paid'}
            batchActions={canManageReceipts ? [
              {
                name: 'Approve Selected',
                fn: (selectedRowsIDs: number[]) => {
                  approveReceipts({ ids: selectedRowsIDs })
                      .unwrap()
                      .then(() => {
                        addToast(`Receipts #${selectedRowsIDs.join(',')} approved`, 'success');
                      })
                      .catch((e: any) => {
                        navigate('/admin/finance/receipts');
                        addToast(getErrorMessage(e), 'error');
                      });
                }
              },
              {
                name: 'Deny Selected',
                fn: (selectedRowsIDs: number[]) => {
                  setShowModal(true);
                  setSelectedIDs(selectedRowsIDs);
                }
              },
              {
                name: 'Delete Selected',
                fn: (selectedRowsIDs: number[]) => {
                  deleteReceipts({ ids: selectedRowsIDs })
                      .unwrap()
                      .then(() => {
                        addToast(`Receipts #${selectedRowsIDs.join(',')} deleted`, 'success');
                      })
                      .catch((e: any) => {
                        navigate('/admin/finance/receipts');
                        addToast(getErrorMessage(e), 'error');
                      });
                }
              }
            ] : undefined}
        />
        <InputModal
            show={showModal}
            title="Reason for Denial"
            message=""
            placeholder=""
            onClose={() => setShowModal(false)}
            onSubmit={(value) => handleDenyReceipts(value)}
        />
      </div>
  );
};
